import { useMemo } from "react";
import { Button, Checkbox, Label, Text, Flex, ErrorMessage, Banner } from "@jsq/ds/core";
import { useFormContext, SubmitButton } from "@jsq/ds/forms";
import { Sizes } from "@jsq/ds/styles";
import { getRemediationTimeValue } from "~/pages/Compliance/CompliancePositionsTable/OnboardingCellRenderer/RemediationInvite/util";
import { formatCountText } from "~/utils/strings";
import {
  selectAllNoneText,
  greyText,
  instructionText,
  portalContactsLabel,
  selectText,
  separatorText,
  buttonSection,
  cancelButtonMargin,
  sentInfoRows,
} from "./styles_vanilla.css";
import { FormValues, RequestLabels } from "./types";

type BaseRequestFormContentProps = {
  emails: string[];
  onClose: () => void;
  loading?: boolean;
  contactsError?: Error;
  submitError?: string | null;
  isResend?: boolean;
  dateSent?: Date;
  requestSentCount?: number;
  labels: RequestLabels;
};

export const BaseRequestFormContent = ({
  emails,
  onClose,
  loading,
  contactsError,
  submitError,
  isResend = false,
  dateSent,
  requestSentCount,
  labels,
}: BaseRequestFormContentProps) => {
  const { values, setFieldValue, errors, touched } = useFormContext<FormValues>();

  const bannerContent = useMemo(() => {
    if (!isResend || !dateSent) return null;

    const result = getRemediationTimeValue(dateSent.toISOString());
    let lastRequestedText: string;
    let sentencePart: string;

    if (result === "Just now") {
      lastRequestedText = "Just now";
      sentencePart = "just now";
    } else if (result === "Sent today") {
      lastRequestedText = "Sent today";
      sentencePart = "today";
    } else {
      const daysAgoText = `${formatCountText(result as number, "day")} ago`;
      lastRequestedText = daysAgoText;
      sentencePart = daysAgoText;
    }

    const isMultiple = (requestSentCount ?? 1) > 1;
    return { lastRequestedText, sentencePart, isMultiple, count: requestSentCount ?? 1 };
  }, [isResend, dateSent, requestSentCount]);

  const handleSelectEmail = (email: string) => {
    const currentSelected = values.selectedEmails || [];
    const newSelected = currentSelected.includes(email)
      ? currentSelected.filter((e) => e !== email)
      : [...currentSelected, email];

    void setFieldValue("selectedEmails", newSelected);
  };

  const handleSelectAllEmails = () => {
    void setFieldValue("selectedEmails", emails);
  };

  const handleSelectNone = () => {
    void setFieldValue("selectedEmails", []);
  };

  const selectedEmails = values.selectedEmails || [];
  const showEmailError = touched.selectedEmails && errors.selectedEmails;
  const emailErrorMessage = errors.selectedEmails?.toString() || "";

  return (
    <>
      {bannerContent && (
        <Banner
          variant="primary"
          title={labels.bannerTitle}
          description={
            <Flex className={sentInfoRows} gap={Sizes.XS}>
              <Text variant="regular-md" color="neutral700">
                {bannerContent.isMultiple
                  ? labels.bannerDescriptionMultiple
                  : labels.bannerDescriptionSingle(bannerContent.sentencePart)}
              </Text>
              <Text variant="regular-md" color="neutral700">
                Requests sent: {bannerContent.count}
              </Text>
              <Text variant="regular-md" color="neutral700">
                Last requested: {bannerContent.lastRequestedText}
              </Text>
            </Flex>
          }
        />
      )}
      <Text variant="regular-md" className={instructionText}>
        {labels.instructionText}
      </Text>
      <Label required={true} className={portalContactsLabel}>
        Portal Admins
      </Label>
      <Flex alignItems="center" gap={Sizes.XS} className={selectAllNoneText}>
        <Text variant="strong-md" className={selectText}>
          Select
        </Text>
        <Button displayAsLink onClick={handleSelectAllEmails}>
          All
        </Button>
        <Text variant="strong-md" className={separatorText}>
          |
        </Text>
        <Button displayAsLink onClick={handleSelectNone}>
          None
        </Button>
      </Flex>
      {emails.length > 0 &&
        emails.map((email: string) => (
          <Flex key={email} className={greyText}>
            <Checkbox
              name={email}
              label={email}
              checked={selectedEmails.includes(email)}
              onChange={() => handleSelectEmail(email)}
            />
          </Flex>
        ))}
      {showEmailError && emailErrorMessage && <ErrorMessage>{emailErrorMessage}</ErrorMessage>}
      {contactsError && (
        <ErrorMessage>Failed to fetch contacts. Please try again later.</ErrorMessage>
      )}
      {submitError && <ErrorMessage>{submitError}</ErrorMessage>}
      <Flex justifyContent="flex-end" className={buttonSection}>
        <Button variant="neutral" onClick={onClose} className={cancelButtonMargin}>
          Cancel
        </Button>
        <SubmitButton isLoading={loading}>Send request</SubmitButton>
      </Flex>
    </>
  );
};
